import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';

import { Heading, TextLink } from './Typography';
import { MessageCard } from './MessageCard';
import { ResendTimer } from '@lendica/components';
import { PhoneFormat } from '../utils/components';
import { phoneSchema, otpSchema, phoneContinuation, phoneVerify, NextStep } from '../api';
import { useAppContainerContext } from './AppContainer';

export const PhoneVerifyFlow = () => {
    const [step, setStep] = useState<1 | 2>(1);
    const [searchParams] = useSearchParams();
    const previousVerifyToken = searchParams.get('token');
    const { loginValue, updateLoginValue, setMsg, setError } = useAppContainerContext();
    const navigate = useNavigate();

    const handlePhoneContinue = async (values: { phone: string }) => {
        try {
            const data = await phoneContinuation(values.phone, previousVerifyToken || undefined);
            if (data.verify_token) {
                setStep(2);
                updateLoginValue({ phone: values.phone, verifyToken: data.verify_token });
            }
        } catch (error: any) {
            console.error(error);
            setError(error.message || 'Something went wrong. Please try again.');
            setTimeout(() => {
                setError(null);
            }, 2500);
        }
    };

    const handleOtpVerify = async (values: { otpCode: string }) => {
        try {
            const data = await phoneVerify(loginValue.verifyToken || '', values.otpCode);
            if (data.next_step === NextStep.CreatePassword) {
                navigate('../create-password');
            }
        } catch (error: any) {
            console.error(error);
            setError(error.message || 'Something went wrong. Please try again.');
            setTimeout(() => {
                setError(null);
            }, 2500);
        }
    };

    const handleResend = async () => {
        try {
            const data = await phoneContinuation(
                loginValue.phone || '',
                previousVerifyToken || undefined
            );
            if (data.verify_token) {
                updateLoginValue({ verifyToken: data.verify_token });
            }
            setMsg(data.message);
            setTimeout(() => {
                setMsg(null);
            }, 2500);
        } catch (error: any) {
            console.error(error);
            setError(error.message || 'Something went wrong. Please try again.');
            setTimeout(() => {
                setError(null);
            }, 2500);
        }
    };

    const handleNavBack = () => {
        setStep(1);
        updateLoginValue({ verifyToken: undefined });
    };

    return (
        <>
            {step === 1 && <Phone handleSubmit={handlePhoneContinue} />}
            {step === 2 && (
                <OtpVerify
                    handleSubmit={handleOtpVerify}
                    handleResend={handleResend}
                    onNavBack={handleNavBack}
                />
            )}
        </>
    );
};

const Phone = ({
    handleSubmit,
}: {
    handleSubmit: (
        { phone }: { phone: string },
        { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
    ) => void;
}) => {
    return (
        <Box
            id="phone-verify"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                width: '100%',
                gap: 2,
            }}
        >
            <Heading>Verify your phone to continue</Heading>
            <MessageCard>Please verify your phone first to continue.</MessageCard>
            <Formik
                initialValues={{
                    phone: '',
                }}
                validationSchema={phoneSchema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                    <Box
                        component={Form}
                        sx={{
                            width: '100%',
                        }}
                    >
                        <Stack sx={{ width: '100%' }}>
                            <TextField
                                label="Phone number"
                                name="phone"
                                fullWidth
                                margin="normal"
                                InputProps={{ inputComponent: PhoneFormat }}
                                inputProps={{ 'data-testid': 'phone' }}
                                value={values.phone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(touched.phone && errors.phone)}
                                helperText={touched.phone && errors.phone ? errors.phone : ' '}
                            />
                        </Stack>
                        <Stack sx={{ width: '100%', mt: 1, mb: 2 }}>
                            <Typography variant="caption" sx={{ textAlign: 'center' }}>
                                We'll send you a one-time code to verify.
                            </Typography>
                        </Stack>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            loading={isSubmitting}
                            fullWidth
                            size="large"
                        >
                            Send code
                        </LoadingButton>
                    </Box>
                )}
            </Formik>
        </Box>
    );
};

const OtpVerify = ({
    handleSubmit,
    handleResend,
    onNavBack,
}: {
    handleSubmit: (
        { otpCode }: { otpCode: string },
        { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
    ) => void;
    handleResend: () => void;
    onNavBack: () => void;
}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                flex: 1,
                width: '100%',
                gap: 2,
            }}
        >
            <IconButton
                size="small"
                onClick={() => {
                    // Track back navigation
                    // pushDL('auth_step', {
                    //     step: 'password_back_to_email_or_phone',
                    //     phone: phone,
                    // });
                    onNavBack();
                }}
                sx={{ ml: -0.5, mb: 1 }}
            >
                <ArrowBackIosIcon />
            </IconButton>
            <Heading>Enter your one-time code</Heading>
            <MessageCard>We've sent a one-time code to your phone.</MessageCard>
            <Formik
                initialValues={{
                    otpCode: '',
                }}
                validationSchema={otpSchema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                    <Box
                        component={Form}
                        sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                height: 'fit-content',
                                mb: 3,
                            }}
                        >
                            <TextField
                                label="One-time code"
                                name="otpCode"
                                fullWidth
                                inputProps={{ 'maxLength': 4, 'data-testid': 'otp' }}
                                value={values.otpCode}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(touched.otpCode && errors.otpCode)}
                                helperText={
                                    touched.otpCode && errors.otpCode ? errors.otpCode : ' '
                                }
                                autoComplete="one-time-code"
                            />
                            <ResendTimer seconds={30} onResend={handleResend}>
                                Resend the code
                            </ResendTimer>
                        </Box>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            loading={isSubmitting}
                            fullWidth
                            size="large"
                        >
                            Next
                        </LoadingButton>
                    </Box>
                )}
            </Formik>
        </Box>
    );
};
